import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { JourneyTimeline } from './JourneyTimeline';
import { Users, Database, PlayCircle, Monitor, Shield, X, ZoomIn, CheckCircle } from 'lucide-react';
const container = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    x: -20
  },
  show: {
    opacity: 1,
    x: 0
  }
};
export function SlideInsideBooth() {
  const [imgError, setImgError] = useState(false);
  const [consentImgError, setConsentImgError] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  return <div className="h-full w-full bg-[#FDF8F8] text-[#2D2926] p-8 md:p-12 flex flex-col" data-id="element-460">
      <JourneyTimeline currentStage={4} data-id="element-461" />

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-6 border-l-4 border-[#C5000F] pl-6" data-id="element-462">
        <div className="flex items-center gap-3 mb-2" data-id="element-463">
          <span className="text-4xl" data-id="element-464">💬</span>
          <h2 className="text-3xl font-bold text-[#2D2926]" data-id="element-465">
            Booth: Crossing the Border
          </h2>
        </div>
        <p className="text-xl text-[#C5000F] font-medium" data-id="element-466">
          Goal: Deep face-to-face engagement — the conversion moment
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_1.2fr] gap-8 min-h-0" data-id="element-467">
        <div className="flex flex-col gap-5 overflow-auto" data-id="element-468">
          <motion.div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100" variants={container} initial="hidden" animate="show" data-id="element-469">
            <h3 className="text-lg font-bold text-[#2D2926] mb-4 flex items-center gap-2" data-id="element-470">
              <span className="w-8 h-1 bg-[#C5000F] rounded-full" data-id="element-471"></span>
              Tactics
            </h3>
            <ul className="space-y-3" data-id="element-472">
              <motion.li variants={item} className="flex items-start gap-3" data-id="element-473">
                <Users className="w-5 h-5 text-[#C5000F] mt-0.5 shrink-0" data-id="element-474" />
                <span className="text-gray-700 text-sm" data-id="element-475">
                  Sales rep–led Snak-based conversations using iPads
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-3" data-id="element-476">
                <Database className="w-5 h-5 text-[#C5000F] mt-0.5 shrink-0" data-id="element-477" />
                <span className="text-gray-700 text-sm" data-id="element-478">
                  Capture HCP preferences, data, and next steps via Input Cues
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-3" data-id="element-479">
                <PlayCircle className="w-5 h-5 text-[#C5000F] mt-0.5 shrink-0" data-id="element-480" />
                <span className="text-gray-700 text-sm" data-id="element-481">
                  Guided demo journeys or objection-handling Snaks™ for rare
                  diseases
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-3" data-id="element-482">
                <Shield className="w-5 h-5 text-[#C5000F] mt-0.5 shrink-0" data-id="element-483" />
                <span className="text-gray-700 text-sm" data-id="element-484">
                  <strong className="text-[#2D2926]" data-id="element-485">
                    Capture explicit opt-in
                  </strong>{' '}
                  for post-congress follow-up during natural conversation flow
                </span>
              </motion.li>
            </ul>
          </motion.div>

          <motion.div className="bg-[#C5000F]/5 p-5 rounded-xl border border-[#C5000F]/10" variants={container} initial="hidden" animate="show" data-id="element-486">
            <h3 className="text-lg font-bold text-[#2D2926] mb-4 flex items-center gap-2" data-id="element-487">
              <span className="w-8 h-1 bg-[#E60012] rounded-full" data-id="element-488"></span>
              Snakker™ Features
            </h3>
            <ul className="space-y-3" data-id="element-489">
              <motion.li variants={item} className="flex items-start gap-3" data-id="element-490">
                <div className="w-2 h-2 rounded-full bg-[#E60012] mt-1.5 shrink-0" data-id="element-491" />
                <span className="text-gray-700 text-sm" data-id="element-492">
                  <strong className="text-[#2D2926]" data-id="element-493">Input Cue™</strong> for
                  name, interest, sign-ups
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-3" data-id="element-494">
                <div className="w-2 h-2 rounded-full bg-[#E60012] mt-1.5 shrink-0" data-id="element-495" />
                <span className="text-gray-700 text-sm" data-id="element-496">
                  <strong className="text-[#2D2926]" data-id="element-497">
                    Feedback + Breadcrumbs
                  </strong>{' '}
                  for interaction quality
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-3" data-id="element-498">
                <div className="w-2 h-2 rounded-full bg-[#E60012] mt-1.5 shrink-0" data-id="element-499" />
                <span className="text-gray-700 text-sm" data-id="element-500">
                  Embedded CTAs: "Book a meeting," "Download clinical data,"
                  etc.
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-3" data-id="element-501">
                <div className="w-2 h-2 rounded-full bg-[#E60012] mt-1.5 shrink-0" data-id="element-502" />
                <span className="text-gray-700 text-sm" data-id="element-503">
                  <strong className="text-[#2D2926]" data-id="element-504">Consent Cue™</strong> —
                  frictionless, compliant opt-in built into the Snak™ flow
                </span>
              </motion.li>
            </ul>
          </motion.div>

          <motion.div className="bg-[#2D2926] p-4 rounded-xl text-white" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} data-id="element-505">
            <div className="flex items-center gap-2 mb-2" data-id="element-506">
              <Shield className="w-5 h-5 text-[#E60012] shrink-0" data-id="element-507" />
              <h3 className="text-sm font-bold" data-id="element-508">Consent & Opt-In</h3>
            </div>
            <p className="text-white/80 text-sm leading-snug" data-id="element-509">
              Every booth interaction includes a{' '}
              <strong className="text-white" data-id="element-510">built-in consent step</strong> —
              HCPs opt in to follow-up communications naturally as part of the
              Snak™ experience. Fully compliant, zero friction.
            </p>
          </motion.div>
        </div>

        <motion.div className="flex flex-col items-center justify-center gap-4 min-h-0 overflow-auto" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} data-id="element-511">
          {/* Main Snak™ screenshot */}
          <a href="https://player.snakker.io/s/lyDA4_N6hU" target="_blank" rel="noopener noreferrer" className="relative w-full rounded-xl overflow-hidden shadow-lg border border-gray-200 bg-gray-100 block transition-shadow hover:shadow-xl hover:border-[#C5000F]/40" data-id="element-512">
            {imgError ? <div className="flex flex-col items-center justify-center w-full h-48 text-gray-400" data-id="element-513">
                <Monitor className="w-12 h-12 mb-3 text-[#C5000F]/40" data-id="element-514" />
                <p className="text-sm font-medium" data-id="element-515">Image could not be loaded</p>
              </div> : <img src="https://cdn.magicpatterns.com/uploads/mqGiMzwaC8ySRv1TPEQBU1/Screenshot_2026-02-11_at_18.10.17.png" alt="Snak™ interactive engagement — specialty selection screen" className="w-full h-auto object-contain" onError={() => setImgError(true)} data-id="element-516" />}
          </a>
          <motion.p className="text-xs text-[#E60012] italic text-center -mt-2" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.6
        }} data-id="element-517">
            Example Snak™ —{' '}
            <a href="https://player.snakker.io/s/lyDA4_N6hU" target="_blank" rel="noopener noreferrer" className="underline text-[#2D2926]" data-id="element-518">
              click to explore
            </a>
          </motion.p>

          {/* Consent campaign flow */}
          <motion.div className="w-full" initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} data-id="element-519">
            <button onClick={() => setLightboxOpen(true)} className="relative w-full rounded-xl overflow-hidden shadow-md border border-purple-200 bg-[#5B2D8E]/5 block group cursor-pointer transition-all hover:shadow-lg hover:border-purple-400" data-id="element-520">
              {consentImgError ? <div className="flex flex-col items-center justify-center w-full h-24 text-gray-400" data-id="element-521">
                  <Shield className="w-8 h-8 mb-2 text-purple-300" data-id="element-522" />
                  <p className="text-xs" data-id="element-523">Image not available</p>
                </div> : <div className="relative" data-id="element-524">
                  <img src="https://cdn.magicpatterns.com/uploads/mttCHDbcJtTmsj7JjPK2ux/Screenshot_2026-03-24_at_09.33.54.png" alt="Consent campaign flow — Social Media → Intro → Quiz → Authentication → HCP Consent" className="w-full h-auto object-cover max-h-[120px]" onError={() => setConsentImgError(true)} data-id="element-525" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center" data-id="element-526">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2 shadow-lg" data-id="element-527">
                      <ZoomIn className="w-5 h-5 text-[#2D2926]" data-id="element-528" />
                    </div>
                  </div>
                </div>}
              <div className="bg-[#5B2D8E] px-3 py-1.5 flex items-center justify-center gap-2" data-id="element-529">
                <Shield className="w-3 h-3 text-white" data-id="element-530" />
                <span className="text-white text-[10px] font-semibold tracking-wide" data-id="element-531">
                  CONSENT CAMPAIGN FLOW — CLICK TO EXPAND
                </span>
              </div>
            </button>
          </motion.div>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence data-id="element-532">
        {lightboxOpen && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-6" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setLightboxOpen(false)} data-id="element-533">
            <motion.div className="relative max-w-[90vw] max-h-[85vh] rounded-2xl overflow-hidden shadow-2xl bg-[#5B2D8E]" initial={{
          scale: 0.85,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.85,
          opacity: 0
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} onClick={e => e.stopPropagation()} data-id="element-534">
              <div className="flex items-center justify-between px-5 py-3 bg-[#5B2D8E]" data-id="element-535">
                <div className="flex items-center gap-2" data-id="element-536">
                  <Shield className="w-4 h-4 text-white" data-id="element-537" />
                  <span className="text-white text-sm font-bold" data-id="element-538">
                    Consent Campaign Flow Example
                  </span>
                </div>
                <button onClick={() => setLightboxOpen(false)} className="text-white/70 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10" data-id="element-539">
                  <X className="w-5 h-5" data-id="element-540" />
                </button>
              </div>
              <img src="https://cdn.magicpatterns.com/uploads/mttCHDbcJtTmsj7JjPK2ux/Screenshot_2026-03-24_at_09.33.54.png" alt="Consent campaign flow — full view" className="w-full h-auto object-contain max-h-[75vh]" data-id="element-541" />
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}